import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { relativeTime, formatCurrency } from "@/lib/format";
import { Modal, Field } from "./Rooms";
import { useProperties } from "@/contexts/PropertyContext";
import {
    Plus, RefreshCw, Trash2, Globe2, Settings2, AlertTriangle,
    CheckCircle2, ListChecks, Activity, Link2, ExternalLink, Copy, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

// ------------------------------------------------------------
// Channel meta (logos = colored mono badge)
// ------------------------------------------------------------
const CHANNEL_META = {
    booking: { label: "Booking.com", color: "bg-[#003B95]" },
    airbnb: { label: "Airbnb", color: "bg-[#FF385C]" },
    expedia: { label: "Expedia", color: "bg-[#FCD34D] text-foreground" },
    agoda: { label: "Agoda", color: "bg-[#5A2B9A]" },
    vrbo: { label: "Vrbo", color: "bg-[#0EA5E9]" },
    google_hotels: { label: "Google Hotels", color: "bg-[#4285F4]" },
    ical: { label: "iCal", color: "bg-[#64748B]" },
};

const SYNC_TYPES = ["availability", "rates", "restrictions", "reservations"];

const TAB_KEYS = [
    { key: "connections", icon: Globe2 },
    { key: "mapping", icon: Link2 },
    { key: "queue", icon: ListChecks },
    { key: "conflicts", icon: AlertTriangle },
];

// ------------------------------------------------------------
// Main
// ------------------------------------------------------------
export default function Channels() {
    const { t } = useTranslation();
    const { properties, activePropertyId } = useProperties();
    const [tab, setTab] = useState("connections");
    const [channels, setChannels] = useState([]);
    const [stats, setStats] = useState(null);
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);

    const loadChannels = async () => {
        const { data } = await api.get("/channels");
        setChannels(data);
    };
    const loadStats = async () => {
        try {
            const { data } = await api.get("/channels/stats/summary");
            setStats(data);
        } catch { /* noop */ }
    };
    useEffect(() => { loadChannels(); loadStats(); }, []);

    const visibleChannels = activePropertyId === "all"
        ? channels
        : channels.filter((c) => c.property_id === activePropertyId);

    const subtitleKey = visibleChannels.length === 1 ? "channels.subtitle" : "channels.subtitle_plural";

    return (
        <>
            <Topbar
                title={t("channels.title")}
                subtitle={t(subtitleKey, { count: visibleChannels.length, healthy: stats?.connected || 0, error: stats?.error || 0 })}
                actions={
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="channels-push-all-btn"
                            onClick={async () => {
                                if (activePropertyId === "all") {
                                    toast.warning(t("channels.push_availability"));
                                    return;
                                }
                                try {
                                    const { data } = await api.post("/channels/push-all", {
                                        property_id: activePropertyId, type: "availability",
                                    });
                                    toast.success(`${data.jobs}`);
                                    await loadStats();
                                } catch (e) {
                                    toast.error(formatApiErrorDetail(e.response?.data?.detail));
                                }
                            }}
                            className="akm-btn-ghost"
                        >
                            <RefreshCw size={14} /> {t("channels.push_availability")}
                        </button>
                        <button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="new-channel-btn">
                            <Plus size={14} /> {t("channels.connect_channel")}
                        </button>
                    </div>
                }
            />

            <div className="flex-1 overflow-auto p-6 space-y-4">
                <LiveChannelsStrip />
                {stats && <StatsRow stats={stats} />}
                <Tabs tab={tab} setTab={setTab} stats={stats} />

                {tab === "connections" && (
                    <ConnectionsTab
                        channels={visibleChannels}
                        properties={properties}
                        onReload={async () => { await loadChannels(); await loadStats(); }}
                        onOpen={(c) => setDetail({ channel: c, tab: "details" })}
                    />
                )}
                {tab === "mapping" && (
                    <MappingTab
                        channels={visibleChannels}
                        properties={properties}
                        onOpen={(c, tt) => setDetail({ channel: c, tab: tt })}
                    />
                )}
                {tab === "queue" && <QueueTab channels={channels} onReload={loadStats} />}
                {tab === "conflicts" && <ConflictsTab />}
            </div>

            {open && (
                <ChannelForm
                    properties={properties}
                    onClose={() => setOpen(false)}
                    onSaved={async () => { setOpen(false); await loadChannels(); await loadStats(); }}
                />
            )}
            {detail && (
                <ChannelDrawer
                    channel={detail.channel}
                    initialTab={detail.tab}
                    properties={properties}
                    onClose={() => setDetail(null)}
                    onReload={async () => { await loadChannels(); await loadStats(); }}
                />
            )}
        </>
    );
}

// ------------------------------------------------------------
// Stats row
// ------------------------------------------------------------
function StatsRow({ stats }) {
    const { t } = useTranslation();
    const items = [
        { k: t("channels.stats.total"), v: stats.total_connections, color: "text-foreground" },
        { k: t("channels.stats.connected"), v: stats.connected, color: "text-emerald-700" },
        { k: t("channels.stats.in_error"), v: stats.error, color: "text-red-700" },
        { k: t("channels.stats.open_conflicts"), v: stats.open_conflicts, color: "text-amber-700" },
        { k: t("channels.stats.failed_jobs"), v: stats.failed_jobs, color: "text-rose-700" },
    ];
    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="channels-stats">
            {items.map((it) => (
                <div key={it.k} className="akm-card p-3">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{it.k}</div>
                    <div className={`mt-1 text-2xl font-semibold tracking-tight ${it.color}`}>{it.v}</div>
                </div>
            ))}
        </div>
    );
}

// ------------------------------------------------------------
// Tabs
// ------------------------------------------------------------
function Tabs({ tab, setTab, stats }) {
    const { t } = useTranslation();
    return (
        <div className="flex items-center gap-1 border-b border-border" data-testid="channels-tabs">
            {TAB_KEYS.map((tt) => {
                const Icon = tt.icon;
                const isActive = tab === tt.key;
                const badge =
                    tt.key === "conflicts" && stats?.open_conflicts
                        ? stats.open_conflicts
                        : tt.key === "queue" && stats?.failed_jobs
                        ? stats.failed_jobs
                        : null;
                return (
                    <button
                        key={tt.key}
                        data-testid={`tab-${tt.key}`}
                        onClick={() => setTab(tt.key)}
                        className={`relative -mb-px flex items-center gap-2 border-b-2 px-3 py-2.5 text-[12px] font-medium tracking-tight transition ${
                            isActive
                                ? "border-[#ff7b02] text-[#ff7b02]"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        <Icon size={13} /> {t(`channels.tabs.${tt.key}`)}
                        {badge ? (
                            <span className={`ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold ${
                                tt.key === "conflicts" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"
                            }`}>{badge}</span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}

// ------------------------------------------------------------
// Tab: Connections
// ------------------------------------------------------------
function ConnectionsTab({ channels, properties, onReload, onOpen }) {
    const { t } = useTranslation();
    const [syncing, setSyncing] = useState(null);

    const sync = async (id) => {
        setSyncing(id);
        try {
            const { data } = await api.post(`/channels/${id}/sync`);
            toast.success(`Synced ${data.synced_count} items across ${Object.keys(data.by_type).length} types`);
            await onReload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSyncing(null);
        }
    };
    const remove = async (id) => {
        if (!window.confirm(t("channels.remove_confirm"))) return;
        try { await api.delete(`/channels/${id}`); toast.success(t("channels.removed")); await onReload(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const test = async (id) => {
        try {
            const { data } = await api.post(`/channels/${id}/test`);
            if (data.ok) toast.success(t("channels.test_ok"));
            else toast.error(t("channels.test_failed", { error: data.error }));
            await onReload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    if (channels.length === 0) {
        return (
            <div className="rounded-[10px] border border-dashed border-border bg-card/50 py-16 text-center" data-testid="channels-empty">
                <Globe2 className="mx-auto mb-2 text-muted-foreground" size={28} />
                <div className="text-sm font-medium">{t("channels.empty.title")}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                    {t("channels.empty.subtitle")}
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="channels-grid">
            {channels.map((c) => {
                const meta = CHANNEL_META[c.channel] || { label: c.channel, color: "bg-slate-500" };
                const property = properties.find((p) => p.id === c.property_id);
                return (
                    <div key={c.id} className="akm-card p-4" data-testid={`channel-card-${c.id}`}>
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                                <div className={`flex h-9 w-9 items-center justify-center rounded-md ${meta.color} text-white text-[10px] font-semibold`}>
                                    <Globe2 size={15} />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold tracking-tight">{c.name}</div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {meta.label} · {property?.name || "—"}
                                    </div>
                                </div>
                            </div>
                            <StatusBadge status={c.status} />
                        </div>

                        <div className="mt-3 space-y-1 text-[11px] text-muted-foreground">
                            <div>{t("channels.last_sync")}: {c.last_sync ? relativeTime(c.last_sync) : t("channels.never")}</div>
                            {c.external_property_id && (
                                <div>{t("channels.ext_property")}: <span className="font-mono">{c.external_property_id}</span></div>
                            )}
                            {c.api_key_hint && (
                                <div>{t("channels.api_key")}: <span className="font-mono">{c.api_key_hint}</span></div>
                            )}
                        </div>

                        <div className="mt-3 grid grid-cols-3 gap-1.5">
                            <button onClick={() => sync(c.id)} disabled={syncing === c.id} className="akm-btn-ghost text-[11px]" data-testid={`channel-sync-${c.id}`}>
                                <RefreshCw size={11} className={syncing === c.id ? "animate-spin" : ""} /> {t("channels.sync")}
                            </button>
                            <button onClick={() => test(c.id)} className="akm-btn-ghost text-[11px]" data-testid={`channel-test-${c.id}`}>
                                <CheckCircle2 size={11} /> {t("channels.test")}
                            </button>
                            <button onClick={() => onOpen(c)} className="akm-btn-ghost text-[11px]" data-testid={`channel-manage-${c.id}`}>
                                <Settings2 size={11} /> {t("channels.manage")}
                            </button>
                        </div>
                        <button onClick={() => remove(c.id)} className="mt-2 w-full akm-btn-ghost text-[11px] text-red-700">
                            <Trash2 size={11} /> {t("channels.remove")}
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

// ------------------------------------------------------------
// Tab: Mapping (list with click-to-open drawer)
// ------------------------------------------------------------
function MappingTab({ channels, properties, onOpen }) {
    const { t } = useTranslation();
    if (channels.length === 0) {
        return (
            <div className="rounded-[10px] border border-dashed border-border bg-card/50 py-12 text-center text-sm text-muted-foreground">
                {t("channels.mapping_empty")}
            </div>
        );
    }
    return (
        <div className="akm-card overflow-hidden">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-4 py-2 text-left">{t("common.channel")}</th>
                        <th className="px-4 py-2 text-left">{t("channels.property")}</th>
                        <th className="px-4 py-2 text-left">{t("common.status")}</th>
                        <th className="px-4 py-2 text-right">{t("common.actions")}</th>
                    </tr>
                </thead>
                <tbody>
                    {channels.map((c) => {
                        const meta = CHANNEL_META[c.channel] || { label: c.channel, color: "bg-slate-500" };
                        const property = properties.find((p) => p.id === c.property_id);
                        return (
                            <tr key={c.id} className="border-t border-border" data-testid={`mapping-row-${c.id}`}>
                                <td className="px-4 py-2">
                                    <div className="flex items-center gap-2">
                                        <div className={`flex h-6 w-6 items-center justify-center rounded-md ${meta.color} text-white`}>
                                            <Globe2 size={11} />
                                        </div>
                                        <div>
                                            <div className="font-medium">{c.name}</div>
                                            <div className="text-[11px] text-muted-foreground">{meta.label}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-2 text-muted-foreground">{property?.name || "—"}</td>
                                <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                                <td className="px-4 py-2 text-right">
                                    <button onClick={() => onOpen(c, "rooms")} className="akm-btn-ghost text-[11px]">
                                        {t("channels.map_rooms")}
                                    </button>
                                    <button onClick={() => onOpen(c, "rates")} className="akm-btn-ghost ml-2 text-[11px]">
                                        {t("channels.map_rates")}
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ------------------------------------------------------------
// Tab: Sync Queue (cross-channel)
// ------------------------------------------------------------
function QueueTab({ channels, onReload }) {
    const [jobs, setJobs] = useState([]);
    const [filter, setFilter] = useState({ status: "", channel: "" });
    const [retrying, setRetrying] = useState(null);

    const load = async () => {
        const p = new URLSearchParams();
        if (filter.status) p.set("status", filter.status);
        if (filter.channel) p.set("channel", filter.channel);
        const { data } = await api.get(`/channels/sync-jobs/all?${p}`);
        setJobs(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.channel]);

    const retry = async (id) => {
        setRetrying(id);
        try {
            const { data } = await api.post(`/channels/sync-jobs/${id}/retry`);
            if (data.ok) toast.success("Retry succeeded");
            else toast.error(`Retry failed: ${data.error}`);
            await load();
            await onReload?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setRetrying(null); }
    };

    const channelById = useMemo(() => Object.fromEntries(channels.map((c) => [c.id, c])), [channels]);

    return (
        <div className="space-y-3" data-testid="queue-tab">
            <div className="flex items-center gap-2">
                <select className="akm-input max-w-[160px]" value={filter.status}
                        onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                        data-testid="queue-filter-status">
                    <option value="">All status</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                </select>
                <select className="akm-input max-w-[180px]" value={filter.channel}
                        onChange={(e) => setFilter({ ...filter, channel: e.target.value })}
                        data-testid="queue-filter-channel">
                    <option value="">All channels</option>
                    {Object.entries(CHANNEL_META).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </select>
                <button onClick={load} className="akm-btn-ghost"><RefreshCw size={12} /> Refresh</button>
                <div className="ml-auto text-[11px] text-muted-foreground">{jobs.length} jobs</div>
            </div>

            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">When</th>
                            <th className="px-3 py-2 text-left">Channel</th>
                            <th className="px-3 py-2 text-left">Type</th>
                            <th className="px-3 py-2 text-left">Direction</th>
                            <th className="px-3 py-2 text-right">Items</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-left">Error</th>
                            <th className="px-3 py-2 text-right">Retries</th>
                            <th className="px-3 py-2 text-right" />
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.map((j) => {
                            const meta = CHANNEL_META[j.channel] || { label: j.channel };
                            const conn = channelById[j.channel_id];
                            return (
                                <tr key={j.id} className="border-t border-border align-top" data-testid={`queue-row-${j.id}`}>
                                    <td className="px-3 py-2 text-[12px] text-muted-foreground whitespace-nowrap">{relativeTime(j.created_at)}</td>
                                    <td className="px-3 py-2">
                                        <div className="text-[12px] font-medium">{meta.label}</div>
                                        <div className="text-[11px] text-muted-foreground">{conn?.name || "—"}</div>
                                    </td>
                                    <td className="px-3 py-2 text-[12px]">{j.sync_type}</td>
                                    <td className="px-3 py-2 text-[12px] text-muted-foreground">{j.direction || "push"}</td>
                                    <td className="px-3 py-2 text-right text-[12px]">{j.synced_count ?? 0}</td>
                                    <td className="px-3 py-2"><StatusBadge status={j.status} /></td>
                                    <td className="px-3 py-2 text-[11px] text-red-700 max-w-[260px] truncate">{j.error || ""}</td>
                                    <td className="px-3 py-2 text-right text-[12px]">{j.retry_count || 0}</td>
                                    <td className="px-3 py-2 text-right">
                                        {j.status === "failed" && (
                                            <button onClick={() => retry(j.id)} disabled={retrying === j.id}
                                                    className="akm-btn-ghost text-[11px]" data-testid={`queue-retry-${j.id}`}>
                                                <RotateCcw size={11} className={retrying === j.id ? "animate-spin" : ""} /> Retry
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {jobs.length === 0 && (
                            <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-muted-foreground">No sync jobs match.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Tab: Conflicts
// ------------------------------------------------------------
function ConflictsTab() {
    const [conflicts, setConflicts] = useState([]);
    const [status, setStatus] = useState("open");
    const [busy, setBusy] = useState(null);

    const load = async () => {
        const { data } = await api.get(`/channels/conflicts/all?status=${status}`);
        setConflicts(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    const resolve = async (id, resolution) => {
        setBusy(id);
        try {
            await api.post(`/channels/conflicts/${id}/resolve`, { resolution });
            toast.success("Conflict resolved");
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(null); }
    };

    return (
        <div className="space-y-3" data-testid="conflicts-tab">
            <div className="flex items-center gap-2">
                {["open", "resolved", "all"].map((s) => (
                    <button key={s} onClick={() => setStatus(s)}
                            className={`rounded-md border px-3 py-1.5 text-[12px] font-medium transition ${
                                status === s
                                    ? "border-[#ff7b02] bg-[#ff7b02]/10 text-[#ff7b02]"
                                    : "border-border text-muted-foreground hover:bg-muted/40"
                            }`}>
                        {s}
                    </button>
                ))}
                <div className="ml-auto text-[11px] text-muted-foreground">{conflicts.length} {status}</div>
            </div>

            {conflicts.length === 0 && (
                <div className="rounded-[10px] border border-dashed border-border bg-card/50 py-12 text-center text-sm text-muted-foreground">
                    No {status === "all" ? "" : status} conflicts. Overbookings are blocked automatically.
                </div>
            )}

            <div className="space-y-2">
                {conflicts.map((c) => (
                    <div key={c.id} className="akm-card p-4" data-testid={`conflict-card-${c.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <AlertTriangle size={14} className="text-amber-600" />
                                    <div className="text-sm font-semibold">
                                        Overbooking blocked · Room {c.room_number || c.room_id?.slice(0, 6)} · {CHANNEL_META[c.channel]?.label || c.channel}
                                    </div>
                                </div>
                                <div className="mt-1 text-[12px] text-muted-foreground">
                                    Incoming: {c.incoming_check_in} → {c.incoming_check_out}
                                </div>
                                {c.existing_summary && (
                                    <div className="text-[12px] text-muted-foreground">Existing: {c.existing_summary}</div>
                                )}
                                {c.incoming_summary && (
                                    <div className="text-[12px] text-muted-foreground">Incoming details: {c.incoming_summary}</div>
                                )}
                                <div className="mt-1 text-[11px] text-muted-foreground">{relativeTime(c.created_at)}</div>
                            </div>
                            {c.status === "open" ? (
                                <div className="flex shrink-0 flex-col gap-1">
                                    <button onClick={() => resolve(c.id, "keep_existing")} disabled={busy === c.id}
                                            className="akm-btn-ghost text-[11px]" data-testid={`conflict-keep-${c.id}`}>
                                        Keep existing
                                    </button>
                                    <button onClick={() => resolve(c.id, "replace_with_incoming")} disabled={busy === c.id}
                                            className="akm-btn-ghost text-[11px] text-red-700" data-testid={`conflict-replace-${c.id}`}>
                                        Replace with incoming
                                    </button>
                                    <button onClick={() => resolve(c.id, "manual")} disabled={busy === c.id}
                                            className="akm-btn-ghost text-[11px]" data-testid={`conflict-manual-${c.id}`}>
                                        Mark resolved manually
                                    </button>
                                </div>
                            ) : (
                                <div className="shrink-0 text-[11px] text-muted-foreground">
                                    Resolved · {c.resolution}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Drawer: per-channel manage (details, rooms, rates, queue, ical)
// ------------------------------------------------------------
function ChannelDrawer({ channel, initialTab, properties, onClose, onReload }) {
    const [tab, setTab] = useState(initialTab || "details");
    const [conn, setConn] = useState(channel);

    const refresh = async () => {
        try { const { data } = await api.get(`/channels/${channel.id}`); setConn(data); }
        catch { /* noop */ }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

    const property = properties.find((p) => p.id === conn.property_id);
    const meta = CHANNEL_META[conn.channel] || { label: conn.channel, color: "bg-slate-500" };

    const DRAWER_TABS = [
        { key: "details", label: "Details" },
        { key: "rooms", label: "Room mapping" },
        { key: "rates", label: "Rate mapping" },
        { key: "queue", label: "Queue" },
        ...(conn.channel === "ical" || conn.ical_url ? [{ key: "ical", label: "iCal" }] : []),
    ];

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="channel-drawer">
            <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
            <div className="relative h-full w-full max-w-[820px] overflow-y-auto bg-background shadow-xl">
                {/* Header */}
                <div className="sticky top-0 z-10 border-b border-border bg-background/95 px-5 py-3.5 backdrop-blur">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className={`flex h-9 w-9 items-center justify-center rounded-md ${meta.color} text-white`}>
                                <Globe2 size={15} />
                            </div>
                            <div>
                                <div className="text-sm font-semibold tracking-tight">{conn.name}</div>
                                <div className="text-[11px] text-muted-foreground">
                                    {meta.label} · {property?.name || "—"}
                                </div>
                            </div>
                            <StatusBadge status={conn.status} />
                        </div>
                        <button onClick={onClose} className="akm-btn-ghost text-[11px]">Close</button>
                    </div>
                    <div className="mt-3 flex items-center gap-1 border-b border-border">
                        {DRAWER_TABS.map((t) => (
                            <button key={t.key} onClick={() => setTab(t.key)}
                                    data-testid={`drawer-tab-${t.key}`}
                                    className={`-mb-px border-b-2 px-3 py-2 text-[12px] font-medium transition ${
                                        tab === t.key
                                            ? "border-[#ff7b02] text-[#ff7b02]"
                                            : "border-transparent text-muted-foreground hover:text-foreground"
                                    }`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="px-5 py-4">
                    {tab === "details" && <DetailsPane conn={conn} onSaved={async () => { await refresh(); await onReload(); }} />}
                    {tab === "rooms" && <RoomsMappingPane conn={conn} />}
                    {tab === "rates" && <RatesMappingPane conn={conn} />}
                    {tab === "queue" && <ConnQueuePane connId={conn.id} />}
                    {tab === "ical" && <IcalPane conn={conn} onReload={async () => { await refresh(); await onReload(); }} />}
                </div>
            </div>
        </div>
    );
}

function DetailsPane({ conn, onSaved }) {
    const [form, setForm] = useState({
        name: conn.name,
        ical_url: conn.ical_url || "",
        ical_export_enabled: conn.ical_export_enabled !== false,
        external_property_id: conn.external_property_id || "",
        api_key: "",
        sync_options: conn.sync_options || { availability: true, rates: true, restrictions: true, reservations: true },
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const body = { ...form };
            if (!body.api_key) delete body.api_key;
            await api.patch(`/channels/${conn.id}`, body);
            toast.success("Saved");
            await onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const pushType = async (type) => {
        try {
            const { data } = await api.post(`/channels/${conn.id}/push`, { type });
            if (data.ok) toast.success(`Pushed ${data.synced_count} ${type} updates`);
            else toast.error(`Push failed: ${data.error}`);
            await onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-4" data-testid="drawer-details">
            <Field label="Connection name">
                <input className="akm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="External property ID">
                <input className="akm-input" value={form.external_property_id}
                       onChange={(e) => setForm({ ...form, external_property_id: e.target.value })}
                       placeholder="e.g. BCOM-1234567" />
            </Field>
            <Field label="API key (will be masked after save)">
                <input className="akm-input" value={form.api_key}
                       onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                       placeholder={conn.api_key_hint || "Enter API key"} />
            </Field>
            <Field label="iCal URL (import)">
                <input className="akm-input" value={form.ical_url}
                       onChange={(e) => setForm({ ...form, ical_url: e.target.value })}
                       placeholder="https://…/calendar.ics" />
            </Field>

            <div>
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Sync options
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                    {SYNC_TYPES.map((k) => (
                        <label key={k} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[12px]">
                            <input type="checkbox" checked={form.sync_options[k] !== false}
                                   onChange={(e) => setForm({ ...form, sync_options: { ...form.sync_options, [k]: e.target.checked } })}
                                   data-testid={`sync-opt-${k}`} />
                            <span className="capitalize">{k}</span>
                        </label>
                    ))}
                </div>
            </div>

            <label className="flex items-center gap-2 text-[12px]">
                <input type="checkbox" checked={form.ical_export_enabled}
                       onChange={(e) => setForm({ ...form, ical_export_enabled: e.target.checked })}
                       data-testid="ical-export-toggle" />
                Enable public iCal export feed
            </label>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                <div className="flex gap-1.5">
                    <button onClick={() => pushType("availability")} className="akm-btn-ghost text-[11px]" data-testid="push-availability-btn">
                        <RefreshCw size={11} /> Push availability
                    </button>
                    <button onClick={() => pushType("rates")} className="akm-btn-ghost text-[11px]" data-testid="push-rates-btn">
                        <RefreshCw size={11} /> Push rates
                    </button>
                    <button onClick={() => pushType("restrictions")} className="akm-btn-ghost text-[11px]" data-testid="push-restrictions-btn">
                        <RefreshCw size={11} /> Push restrictions
                    </button>
                </div>
                <button onClick={save} disabled={saving} className="akm-btn-primary" data-testid="details-save-btn">
                    {saving ? "Saving…" : "Save changes"}
                </button>
            </div>
        </div>
    );
}

function RoomsMappingPane({ conn }) {
    const [rooms, setRooms] = useState([]);
    const [mapping, setMapping] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const [{ data: rs }, { data: ms }] = await Promise.all([
            api.get(`/rooms?property_id=${conn.property_id}`),
            api.get(`/channels/${conn.id}/mappings/rooms`),
        ]);
        setRooms(rs);
        const map = Object.fromEntries(ms.map((m) => [m.room_id, m]));
        setMapping(rs.map((r) => ({
            room_id: r.id, room_number: r.number, room_type: r.room_type,
            external_id: map[r.id]?.external_id || "",
            external_name: map[r.id]?.external_name || "",
        })));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/channels/${conn.id}/mappings/rooms`, {
                mappings: mapping.filter((m) => m.external_id),
            });
            toast.success("Room mappings saved");
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-3" data-testid="drawer-rooms">
            <div className="text-[12px] text-muted-foreground">
                Map each PMS room to the corresponding {CHANNEL_META[conn.channel]?.label || conn.channel} room ID.
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Room</th>
                            <th className="px-3 py-2 text-left">Type</th>
                            <th className="px-3 py-2 text-left">External ID</th>
                            <th className="px-3 py-2 text-left">External name</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mapping.map((m, i) => (
                            <tr key={m.room_id} className="border-t border-border">
                                <td className="px-3 py-2 font-medium">{m.room_number}</td>
                                <td className="px-3 py-2 text-muted-foreground">{m.room_type}</td>
                                <td className="px-3 py-2">
                                    <input className="akm-input" value={m.external_id}
                                           onChange={(e) => setMapping((prev) => {
                                               const next = [...prev]; next[i] = { ...next[i], external_id: e.target.value }; return next;
                                           })}
                                           placeholder="e.g. 12345" data-testid={`map-ext-id-${i}`} />
                                </td>
                                <td className="px-3 py-2">
                                    <input className="akm-input" value={m.external_name}
                                           onChange={(e) => setMapping((prev) => {
                                               const next = [...prev]; next[i] = { ...next[i], external_name: e.target.value }; return next;
                                           })}
                                           placeholder="optional" />
                                </td>
                            </tr>
                        ))}
                        {rooms.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">No rooms in this property.</td></tr>}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-end">
                <button onClick={save} disabled={saving} className="akm-btn-primary" data-testid="rooms-mapping-save">
                    {saving ? "Saving…" : "Save mappings"}
                </button>
            </div>
        </div>
    );
}

function RatesMappingPane({ conn }) {
    const [types, setTypes] = useState([]);
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const [{ data: rs }, { data: ms }] = await Promise.all([
            api.get(`/rooms?property_id=${conn.property_id}`),
            api.get(`/channels/${conn.id}/mappings/rates`),
        ]);
        const uniqTypes = Array.from(new Set(rs.map((r) => r.room_type))).sort();
        setTypes(uniqTypes);
        const map = Object.fromEntries(ms.map((m) => [m.room_type, m]));
        setRows(uniqTypes.map((t) => ({
            room_type: t,
            external_rate_plan_id: map[t]?.external_rate_plan_id || "",
            external_rate_plan_name: map[t]?.external_rate_plan_name || "",
            price_modifier_pct: map[t]?.price_modifier_pct || 0,
        })));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/channels/${conn.id}/mappings/rates`, {
                mappings: rows.filter((r) => r.external_rate_plan_id),
            });
            toast.success("Rate mappings saved");
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-3" data-testid="drawer-rates">
            <div className="text-[12px] text-muted-foreground">
                Map each room type to a channel rate plan. Use price modifier to mark up/down by % from PMS base price.
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Room type</th>
                            <th className="px-3 py-2 text-left">Rate plan ID</th>
                            <th className="px-3 py-2 text-left">Rate plan name</th>
                            <th className="px-3 py-2 text-right">Modifier %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.room_type} className="border-t border-border">
                                <td className="px-3 py-2 font-medium">{r.room_type}</td>
                                <td className="px-3 py-2">
                                    <input className="akm-input" value={r.external_rate_plan_id}
                                           onChange={(e) => setRows((prev) => {
                                               const next = [...prev]; next[i] = { ...next[i], external_rate_plan_id: e.target.value }; return next;
                                           })}
                                           placeholder="e.g. STD-NRF" data-testid={`rate-id-${i}`} />
                                </td>
                                <td className="px-3 py-2">
                                    <input className="akm-input" value={r.external_rate_plan_name}
                                           onChange={(e) => setRows((prev) => {
                                               const next = [...prev]; next[i] = { ...next[i], external_rate_plan_name: e.target.value }; return next;
                                           })}
                                           placeholder="Standard Non-Refundable" />
                                </td>
                                <td className="px-3 py-2">
                                    <input type="number" step="0.1" className="akm-input text-right" value={r.price_modifier_pct}
                                           onChange={(e) => setRows((prev) => {
                                               const next = [...prev]; next[i] = { ...next[i], price_modifier_pct: Number(e.target.value) }; return next;
                                           })} />
                                </td>
                            </tr>
                        ))}
                        {types.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">No room types yet.</td></tr>}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-end">
                <button onClick={save} disabled={saving} className="akm-btn-primary" data-testid="rates-mapping-save">
                    {saving ? "Saving…" : "Save mappings"}
                </button>
            </div>
        </div>
    );
}

function ConnQueuePane({ connId }) {
    const [jobs, setJobs] = useState([]);
    const [retrying, setRetrying] = useState(null);

    const load = async () => {
        const { data } = await api.get(`/channels/${connId}/queue?limit=50`);
        setJobs(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const retry = async (id) => {
        setRetrying(id);
        try {
            const { data } = await api.post(`/channels/sync-jobs/${id}/retry`);
            if (data.ok) toast.success("Retry succeeded");
            else toast.error(`Retry failed: ${data.error}`);
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setRetrying(null); }
    };

    return (
        <div className="space-y-2" data-testid="drawer-queue">
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-muted-foreground">Last 50 sync jobs for this connection.</div>
                <button onClick={load} className="akm-btn-ghost text-[11px]"><RefreshCw size={11} /> Refresh</button>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">When</th>
                            <th className="px-3 py-2 text-left">Type</th>
                            <th className="px-3 py-2 text-right">Items</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-left">Error</th>
                            <th className="px-3 py-2 text-right" />
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.map((j) => (
                            <tr key={j.id} className="border-t border-border">
                                <td className="px-3 py-2 text-[12px] text-muted-foreground">{relativeTime(j.created_at)}</td>
                                <td className="px-3 py-2 text-[12px]">{j.sync_type}</td>
                                <td className="px-3 py-2 text-right text-[12px]">{j.synced_count ?? 0}</td>
                                <td className="px-3 py-2"><StatusBadge status={j.status} /></td>
                                <td className="px-3 py-2 text-[11px] text-red-700 max-w-[260px] truncate">{j.error || ""}</td>
                                <td className="px-3 py-2 text-right">
                                    {j.status === "failed" && (
                                        <button onClick={() => retry(j.id)} disabled={retrying === j.id} className="akm-btn-ghost text-[11px]">
                                            <RotateCcw size={11} className={retrying === j.id ? "animate-spin" : ""} /> Retry
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {jobs.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No jobs yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function IcalPane({ conn, onReload }) {
    const [importing, setImporting] = useState(false);
    const exportUrl = `${window.location.origin.replace(/\/$/, "")}/api/channels/ical/${conn.id}.ics`;

    const doImport = async () => {
        setImporting(true);
        try {
            const { data } = await api.post(`/channels/${conn.id}/ical/import`);
            if (data.ok) {
                toast.success(`iCal import: ${data.imported} new, ${data.conflicts} conflicts, ${data.skipped} skipped`);
            } else {
                toast.error(data.error || "Import failed");
            }
            await onReload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setImporting(false); }
    };

    return (
        <div className="space-y-4" data-testid="drawer-ical">
            <div>
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Import (pull)</div>
                <div className="rounded-md border border-border bg-card/40 p-3 text-[12px]">
                    {conn.ical_url ? (
                        <>
                            <div className="font-mono break-all text-muted-foreground">{conn.ical_url}</div>
                            <button onClick={doImport} disabled={importing} className="akm-btn-primary mt-2 text-[11px]" data-testid="ical-import-btn">
                                <RefreshCw size={11} className={importing ? "animate-spin" : ""} /> Import now
                            </button>
                        </>
                    ) : (
                        <div className="text-muted-foreground">No iCal URL configured. Edit details and add one.</div>
                    )}
                </div>
            </div>

            <div>
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Export (push)</div>
                <div className="rounded-md border border-border bg-card/40 p-3">
                    <div className="text-[12px] text-muted-foreground">
                        Share this feed with channels that import iCal (e.g. Airbnb, Vrbo).
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <input readOnly className="akm-input flex-1 font-mono text-[11px]" value={exportUrl} />
                        <button onClick={() => { navigator.clipboard.writeText(exportUrl); toast.success("Copied"); }}
                                className="akm-btn-ghost text-[11px]" data-testid="ical-copy-btn">
                            <Copy size={11} /> Copy
                        </button>
                        <a href={exportUrl} target="_blank" rel="noreferrer" className="akm-btn-ghost text-[11px]">
                            <ExternalLink size={11} /> Open
                        </a>
                    </div>
                    {conn.ical_export_enabled === false && (
                        <div className="mt-2 text-[11px] text-amber-700">
                            Export is currently disabled. Re-enable from Details.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// New connection form
// ------------------------------------------------------------
function ChannelForm({ properties, onClose, onSaved }) {
    const [catalog, setCatalog] = useState([]);
    const [form, setForm] = useState({
        property_id: properties[0]?.id || "",
        channel: "booking",
        name: "",
        ical_url: "",
        api_key: "",
        api_secret: "",
        external_property_id: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/channels/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = { ...form };
            if (!body.ical_url) delete body.ical_url;
            if (!body.api_key) delete body.api_key;
            if (!body.api_secret) delete body.api_secret;
            if (!body.external_property_id) delete body.external_property_id;
            await api.post("/channels", body);
            toast.success("Channel connected");
            onSaved();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const needsApiKey = form.channel !== "ical";
    const supportsIcal = form.channel === "ical" || form.channel === "airbnb" || form.channel === "vrbo";

    return (
        <Modal title="Connect channel" onClose={onClose} width="max-w-lg">
            <form onSubmit={submit} className="space-y-3" data-testid="new-channel-form">
                <Field label="Property">
                    <select className="akm-input" value={form.property_id}
                            onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                            data-testid="form-property">
                        {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </Field>
                <Field label="Channel">
                    <select className="akm-input" value={form.channel}
                            onChange={(e) => setForm({ ...form, channel: e.target.value })}
                            data-testid="form-channel">
                        {(catalog.length ? catalog : Object.entries(CHANNEL_META).map(([k, v]) => ({ key: k, label: v.label })))
                            .map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                </Field>
                <Field label="Display name">
                    <input className="akm-input" required value={form.name}
                           onChange={(e) => setForm({ ...form, name: e.target.value })}
                           placeholder="e.g. Booking.com — Tirana" data-testid="form-name" />
                </Field>
                {needsApiKey && (
                    <>
                        <Field label="External property ID">
                            <input className="akm-input" value={form.external_property_id}
                                   onChange={(e) => setForm({ ...form, external_property_id: e.target.value })}
                                   placeholder="e.g. 1234567" data-testid="form-ext-id" />
                        </Field>
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="API key">
                                <input className="akm-input" value={form.api_key}
                                       onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                                       placeholder="••••••••" data-testid="form-api-key" />
                            </Field>
                            <Field label="API secret">
                                <input className="akm-input" value={form.api_secret}
                                       onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
                                       placeholder="••••••••" />
                            </Field>
                        </div>
                    </>
                )}
                {supportsIcal && (
                    <Field label={`iCal URL${needsApiKey ? " (optional fallback)" : ""}`}>
                        <input className="akm-input" value={form.ical_url}
                               onChange={(e) => setForm({ ...form, ical_url: e.target.value })}
                               placeholder="https://…/calendar.ics" data-testid="form-ical-url" />
                    </Field>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="form-submit">
                        {saving ? "Saving…" : "Connect"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// ------------------------------------------------------------
// LiveChannelsStrip — real OTA connections status (invisible provider)
// ------------------------------------------------------------
function LiveChannelsStrip() {
    const { t } = useTranslation();
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get("/channex/my/channels").then(({ data }) => setRows(data)).catch(() => setRows([]));
    }, []);
    if (!rows || rows.length === 0) return null;
    const anyConnected = rows.some((r) => r.connected);
    return (
        <div className="akm-card p-4" data-testid="live-channels-strip">
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("channels.live_title", "Distribution channels")}
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {anyConnected
                            ? t("channels.live_help", "Reservations from these channels are imported automatically.")
                            : t("channels.live_none", "No channels connected. Contact Akomoda support to activate.")}
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {rows.map((c) => (
                    <div key={c.code}
                         data-testid={`live-channel-${c.code}`}
                         className={`relative overflow-hidden rounded-md border p-3 transition-all ${
                             c.connected
                                 ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-500/30 dark:bg-emerald-500/5"
                                 : "border-border bg-muted/20 opacity-70"}`}>
                        <div className="flex items-start justify-between">
                            <div className={`text-[13px] font-semibold ${c.connected ? "" : "text-muted-foreground"}`}>
                                {c.label}
                            </div>
                            <span className={`h-2 w-2 rounded-full ${c.connected ? "bg-emerald-500" : "bg-slate-300"}`} />
                        </div>
                        {c.connected && (
                            <div className="mt-2 space-y-0.5">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                    {t("channels.this_month", "This month")}
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <div className="text-lg font-semibold tracking-tight">{c.bookings_this_month}</div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {t("channels.bookings", "bookings")}
                                    </div>
                                </div>
                                {c.revenue_this_month > 0 && (
                                    <div className="text-[11px] text-emerald-700 dark:text-emerald-300">
                                        €{Number(c.revenue_this_month).toFixed(2)}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

